// DOM readiness gate for boot().
//
// The customer embeds `<script src=".../widget.js" async>` anywhere in <head> or <body>.
// An async script runs as soon as it has downloaded — possibly while the parser is still
// in <head>, before any of the host's search box / results container / carousel slots
// exist. boot() therefore waits for the document to be parsed before evaluating any
// selector / activation predicate. Deliberately NOT `load`: DOMContentLoaded (or an
// already-parsed document) is all the widgets need, and waiting for images etc. would
// only delay them.
//
// What runs BEFORE this wait (boot.ts, iteration 20's "head start"): every step that doesn't
// read the host page — the light stylesheet link, the experiment arm (localStorage + URL) + its
// <html> attributes + exposure event, the early-click guards (./click-guard.ts, iterations 14 + 22,
// whose delegated document listeners swallow activations of the click widgets' elements during
// this wait so each widget can open() for them afterwards), the SDKs' configure(), the
// script-start activation context, the search preparation (products/search.ts prepareSearch:
// results' verdict, the hide <style>, the results prefetch), the shadow stylesheet preload /
// prefetch + preconnects (./warmup.ts), the analytics engine's start() (its identification waits
// for this promise) and the building of the per-widget pipelines, each of which then awaits
// this readiness on its own (boot.ts `readiness`).
//
// Import-free (like light-styles.ts), so it unit-tests under plain Node with a stub
// document (packages/sdk/test/boot.test.ts).

// Resolves once the document is parsed: immediately when `readyState` is already
// "interactive"/"complete" (the common case for an async script, and always the case
// for the dev harness), else on the first DOMContentLoaded.
export function whenDomReady(doc: Document = document): Promise<void> {
  if (doc.readyState !== "loading") return Promise.resolve();
  return new Promise((resolve) => {
    doc.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
  });
}
